import { createContext, useContext, useEffect, useState, useRef } from "react";
import { Link, Outlet, useLocation, useMatch, useNavigate } from "react-router-dom";
import { AuthContext, UserProfile } from "../context/auth";
import { collection, getDocs, query, doc, updateDoc, deleteDoc, setDoc, deleteField} from "firebase/firestore";
import { db } from "../firebase";
import '../styles/root.css';
import Sequence from '../components/sequences/sequences';
import Groups from '../components/groups';
import Notifications from "../components/notifications/notifications";
import World from "../components/world/world";
import Assessments from "../components/assessments/assessments";
import Plan from "../components/plan/plan";
import Forms from "../components/forms/forms";

import { useUserLevel, AdminLevel } from "../context/admin";
import 'react-tooltip/dist/react-tooltip.css';
import AccessCodes from "../components/AccessCodes";
import Leaderboard from "../components/leaderboard/Leaderboard";
import DeviceManager from "../components/moduleDatabase/DeviceManager";
import ControlPanel from "../components/controlPanel/ControlPanel";
import NoGroupUsers from "../components/noGroupUsers/noGroupUsers";
import AppsModal from "../components/AppsModal";

async function updateUsersCollection() {
    try {
        const usersCollection = collection(db, "users");
        const snapshot = await getDocs(usersCollection);

        if (snapshot.empty) {
            console.log("No documents found in the users collection.");
            return;
        }

        for (const document of snapshot.docs) {
            const userRef = doc(db, "users", document.id);
            await updateDoc(userRef, {
                recoveryPlanDate: deleteField(),
                features: deleteField()
            });

            console.log(`Updated document ID: ${document.id} - Deleted recoveryPlanDate and features`);
        }

        console.log("Users collection update completed.");
    } catch (error) {
        console.error("Error updating users collection: ", error);
    }
}

//updateUsersCollection();

const getUsers = async (uid: string) => {
  const docT = await getDocs(collection(db, 'users'));
  return docT.docs.map(doc => ({ ...doc.data(), id: doc.id } as UserProfile));
}

export default function Root() {
  const { currentUserLevel, setCurrentUserLevel } = useUserLevel();
  const { currentUser, signOut } = useContext(AuthContext);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [currentUserProfile, setCurrentUserProfile] = useState<UserProfile | null>(null); // Define currentUserProfile state
  const [searchQuery, setSearchQuery] = useState('');
  const [groupId, setGroupId] = useState('');
  const [allGroups, setAllGroups] = useState<string[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const [isUserDropDownOpen, setIsUserDropDownOpen] = useState(false);
  const [isGroupDropDownOpen, setIsGroupDropDownOpen] = useState(false);
  const [isGroupMember, setIsGroupMember] = useState(false);

  const userDropdownRef = useRef<HTMLDivElement>(null);
  const groupDropdownRef = useRef<HTMLDivElement>(null);
  const [isAssessmentsVisible, setIsAssessmentsVisible] = useState(false);
  const [userName, setUserName] = useState<string>('Search User');
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login', { state: { from: location.pathname + location.search } });
    }
  }, [currentUser, navigate, location.pathname, location.search]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setIsUserDropDownOpen(false);
      }
      if (groupDropdownRef.current && !groupDropdownRef.current.contains(event.target as Node)) {
        setIsGroupDropDownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const checkUserLevel = async () => {
      if (currentUser) {
        //TODO improve this later
        const superAdminEmails = [
          "willflux306@gmail.com",
          "steve@myovolt.com",
          "dianne@myovolt.com",
          "steve@sentrixtechnology.com",
          "info@myovolt.com",
          "myovolt@gmail.com",
        ];
        const isSuperAdmin = superAdminEmails.includes(currentUser.email ?? '');
        setCurrentUserLevel(isSuperAdmin ? AdminLevel.SUPER_ADMIN : AdminLevel.USER);
        console.log(`Access: ${isSuperAdmin ? 'Super_Admin' : 'User'}`);

        // Determine group membership from each group's current member list
        const groupsSnapshot = await getDocs(query(collection(db, "groups")));
        const isMember = groupsSnapshot.docs.some(doc =>
          (doc.data().Members ?? []).includes(currentUser.email)
        );
        setIsGroupMember(isMember);

        // Fetch the users after determining the user level
        const users = await getUsers(currentUser.uid);
        setUsers(users);
        const uniqueGroupIds = [...new Set(users.map(user => user.groupId).filter(level => level !== null))];
        setAllGroups(uniqueGroupIds as string[]);

        const currentUserProfile = users.find(user => user.id === currentUser.uid);
        if (currentUserProfile) {
          setCurrentUserProfile(currentUserProfile); // Set currentUserProfile state
        }
      }
    };
  
    checkUserLevel();


  }, [currentUser, setCurrentUserLevel]);


  // The URL is the source of truth for which user is shown; the header bar
  // and the saved "last viewed" user follow it so they can never disagree.
  const routeUserId = useMatch("/user/:id")?.params.id ?? null;

  useEffect(() => {
    if (routeUserId || !currentUser) return;
    // Bare "/" — reopen the last viewed user, if any.
    try {
      const stored = JSON.parse(localStorage.getItem("selectedUser") ?? "null");
      if (stored?.id) navigate(`/user/${stored.id}`, { replace: true });
    } catch {
      localStorage.removeItem("selectedUser");
    }
  }, [routeUserId, currentUser, navigate]);

  useEffect(() => {
    setSelectedUserId(routeUserId);
    if (!routeUserId) {
      setSelectedUser(null);
      return;
    }
    const found = users.find(u => u.id === routeUserId);
    if (found) {
      setSelectedUser(found);
      setUserName(found.name);
      localStorage.setItem("selectedUser", JSON.stringify(found));
    } else {
      // Users not loaded yet (or not in this admin's list): only keep a saved
      // user in the bar if it is the one actually being shown.
      setSelectedUser(prev => (prev?.id === routeUserId ? prev : null));
    }
  }, [routeUserId, users]);

  const handleUserSelect = (userId: string, userName: string) => {
    setUserName(userName);
    setSelectedUserId(userId);

    // Find user object
    const foundUser = users.find(u => u.id === userId) || null;
    setSelectedUser(foundUser);

    navigate(`/user/${userId}`);
    setIsUserDropDownOpen(false);
    setSearchQuery('');
    
    // store in local storage
    if (foundUser) {
      localStorage.setItem("selectedUser", JSON.stringify(foundUser));

    }
  };

  const handleGroupSelect = (groupId: string) => {
    setGroupId(groupId);
    setIsGroupDropDownOpen(false);
  };

  const handleCancel = () => {
    setIsAssessmentsVisible(false);
  };

  const toggleAssessmentsVisibility = () => {
    setIsAssessmentsVisible((prev) => !prev);
  };

  const filteredUsers = users.filter(user =>
    (user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (user.email ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.id.toLowerCase().includes(searchQuery.toLowerCase())) &&
    (!groupId || user.groupId?.toLowerCase().includes(groupId.toLowerCase()))
  );

  if (!currentUser || !currentUser.email) {
    return <div></div>;
  }


  return (
    <div className="min-h-screen flex flex-col">
      {/* ── Navbar ── */}
      <div className="navbar bg-white border-b border-gray-200 sticky top-0 z-20 px-3 gap-2 overflow-visible">
        <div className="navbar-start gap-1 flex-wrap">
          <Groups isGroupMember={isGroupMember} groupId={currentUserProfile?.groupId} />
          {currentUserLevel !== AdminLevel.USER && <Sequence users={filteredUsers} uid={currentUser.uid} />}
          {currentUserLevel !== AdminLevel.USER && <Plan />}
          {currentUserLevel !== AdminLevel.USER && <Forms users={filteredUsers} uid={currentUser.uid} />}
          {currentUserLevel === AdminLevel.SUPER_ADMIN && <World tempData={[]} />}
          {isAssessmentsVisible && <Assessments onClose={handleCancel} />}
        </div>

        <div className="navbar-center">
          <button
            className="text-lg font-bold tracking-tight text-gray-800 hover:text-MyovoltPurple transition-colors"
            onClick={() => { window.location.href = 'https://myovolt.com'; }}
          >
            Myovolt
          </button>
        </div>

        <div className="navbar-end flex items-center gap-2">
          {currentUserLevel !== AdminLevel.USER && (
            <div className="relative" ref={userDropdownRef}>
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search users…"
                  className="input input-bordered input-sm pl-9 w-48"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setIsUserDropDownOpen(true)}
                />
              </div>
              {isUserDropDownOpen && (
                <ul className="absolute right-0 mt-1 p-1 shadow-lg bg-white border border-gray-100 rounded-xl w-72 z-50 max-h-64 overflow-y-auto">
                  {filteredUsers.length === 0
                    ? <li className="px-3 py-2 text-sm text-gray-400">No users found</li>
                    : filteredUsers
                        .slice()
                        .sort((a, b) => (a.email ?? '').localeCompare(b.email ?? ''))
                        .map((user) => (
                          <li
                            key={user.id}
                            className="px-3 py-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                            onClick={() => handleUserSelect(user.id, user.name)}
                          >
                            <p className="text-sm font-medium text-gray-900">{user.name}</p>
                            <p className="text-xs text-gray-500">{user.email}</p>
                            {user.groupId && <p className="text-xs text-gray-400">Group: {user.groupId}</p>}
                          </li>
                        ))
                  }
                </ul>
              )}
            </div>
          )}

          {currentUserLevel !== AdminLevel.USER && (
            <div className="relative" ref={groupDropdownRef}>
              <input
                type="text"
                placeholder="Filter group…"
                className="input input-bordered input-sm w-36"
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
                onFocus={() => setIsGroupDropDownOpen(true)}
              />
              {isGroupDropDownOpen && (
                <ul className="absolute right-0 mt-1 p-1 shadow-lg bg-white border border-gray-100 rounded-xl w-48 z-50 max-h-60 overflow-y-auto">
                  {allGroups.length === 0
                    ? <li className="px-3 py-2 text-sm text-gray-400">No groups found</li>
                    : allGroups.map((group, index) => (
                        <li
                          key={index}
                          className="px-3 py-2 rounded-lg hover:bg-gray-50 cursor-pointer text-sm text-gray-800"
                          onClick={() => handleGroupSelect(group)}
                        >
                          {group}
                        </li>
                      ))
                  }
                </ul>
              )}
            </div>
          )}

          <div className="dropdown dropdown-end">
            <div tabIndex={0} role="button" className="btn btn-ghost btn-circle">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </div>
            <ul tabIndex={0} className="menu menu-sm dropdown-content mt-2 z-[999] p-2 shadow-lg bg-white border border-gray-100 rounded-xl w-52">
              <li><a className="text-sm text-gray-700 rounded-lg" onClick={signOut}>Logout</a></li>
              <div className="divider my-1" />
              <AppsModal />
              {currentUserLevel === AdminLevel.SUPER_ADMIN && (
                <>
                  <div className="divider my-1" />
                  <AccessCodes />
                  <Leaderboard />
                  <DeviceManager />
                  <ControlPanel />
                  <NoGroupUsers />
                </>
              )}
            </ul>
          </div>

          <Notifications uid={currentUser.uid} />
        </div>
      </div>

      {/* ── Selected-user context bar ── */}
      {selectedUser && (
        <div className="sticky top-[64px] z-10 bg-indigo-50 border-b border-indigo-100 px-4 py-2 flex flex-wrap gap-x-6 gap-y-1 text-sm">
          <span><span className="font-semibold text-gray-600">User:</span> <span className="text-gray-800">{selectedUser.name}</span></span>
          <span><span className="font-semibold text-gray-600">Email:</span> <span className="text-gray-800">{selectedUser.email}</span></span>
          <span className="text-gray-400 text-xs self-center font-mono">{selectedUser.id}</span>
        </div>
      )}

      {/* ── Page content ── */}
      <main className="flex-1 pb-16">
        <Outlet context={{ selectedUserId }} />
      </main>

      {/* ── Footer ── */}
      <footer className="border-t border-gray-100 bg-white px-6 py-4 flex items-center justify-between text-sm text-gray-400">
        <span className="font-semibold text-gray-600">Myovolt</span>
        <div className="flex gap-4">
          <a href="https://twitter.com/myovolt" aria-label="Twitter" className="hover:text-gray-600 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" className="fill-current">
              <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
            </svg>
          </a>
          <a href="https://youtube.com/myovolt" aria-label="YouTube" className="hover:text-gray-600 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" className="fill-current">
              <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
            </svg>
          </a>
          <a href="https://facebook.com/myovolt" aria-label="Facebook" className="hover:text-gray-600 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" className="fill-current">
              <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z" />
            </svg>
          </a>
        </div>
      </footer>
    </div>
  );
}
